import { ChangeEvent, FocusEvent, useState } from 'react';

import { filterObj } from '@/utils/objects';

type FormValues<T> = {
  [K in keyof T]: T[K];
};
type FormTouched<T> = Partial<{
  [K in keyof T]: boolean;
}>;
type FormErrors<T> = {
  [K in keyof T]?: string;
};

type GetFieldPropsReturn<T, K extends keyof T> = {
  name: K;
  value: T[K];
  onChange: (e: ChangeEvent<any>) => void;
  onBlur: (e: FocusEvent<any>) => void;
  error?: string;
};

export type UseFormActions = { resetForm: () => void };

export type UseFormReturn<T> = {
  values: T;
  errors: FormErrors<T>;
  handleSubmit: (
    callback: (formState: T, formActions: UseFormActions) => void
  ) => React.FormEventHandler;
  getFieldProps: <K extends keyof T>(field: K) => GetFieldPropsReturn<T, K>;
  setFieldValue: <K extends keyof T>(
    name: K,
    value: T[K],
    shouldValidate?: boolean
  ) => void;
  isValid: boolean;
  isDirty: boolean;
} & UseFormActions;

export type FormValidator<T> = Partial<
  Record<keyof T, (value: T[keyof T]) => string | undefined>
>;

type UseFormProps<T extends Record<string, unknown>> = {
  initialValues: FormValues<T>;
  validator: FormValidator<T>;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
};

export function useForm<T extends Record<string, any>>({
  initialValues,
  validator,
  validateOnBlur,
  validateOnChange,
}: UseFormProps<T>): UseFormReturn<T> {
  const [values, setValues] = useState<FormValues<T>>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [touched, setTouched] = useState<FormTouched<T>>({});
  const [isDirty, setIsDirty] = useState(false);

  function validateField<K extends keyof T>(name: K, value: T[K]) {
    const validate = validator[name];
    if (!validate) return;
    return validate(value);
  }

  const validateForm = (form: T) => {
    if (validator) {
      const fieldNames = Object.keys(form) as (keyof T)[];
      const newErrors: FormErrors<T> = {};
      for (const fieldName of fieldNames) {
        const fieldValue = form[fieldName];
        const validate = validator[fieldName];

        if (!validate) continue;

        if (fieldName in form) {
          const fieldError = validate(fieldValue);

          if (!fieldError) continue;
          newErrors[fieldName] = fieldError;
        }
      }
      return newErrors;
    }

    return {};
  };

  function handleChange(e: ChangeEvent<any>) {
    const name = e.target.name;
    let value;

    if (e.target.type === 'checkbox') {
      value = e.target.checked;
    } else if (e.target.type === 'file') {
      value = e.target.files ? Array.from(e.target.files) : [];
    } else {
      value = e.target.value ?? e.currentTarget.value;
    }

    const nextState = {
      ...values,
      [name]: value,
    };

    setValues(nextState);
    if (validateOnChange) setErrors(validateForm(nextState));
    if (!isDirty) setIsDirty(true);
  }

  function handleBlur(e: FocusEvent<any>) {
    const { name } = e.target ?? e.currentTarget;

    setTouched((prevState) => ({
      ...prevState,
      [name]: true,
    }));

    if (validateOnBlur) {
      const errors = validateForm(values);
      setErrors(errors);
    }
  }

  function handleSubmit(
    callback: (values: T, actions: { resetForm: () => void }) => void
  ) {
    return function (event: React.FormEvent<HTMLFormElement>) {
      event.preventDefault();

      const newTouched: { [K in keyof T]: boolean } = Object.fromEntries(
        Object.keys(values).map((key) => [key, true])
      ) as { [K in keyof T]: boolean };
      setTouched(newTouched);

      const errors = validateForm(values);
      setErrors(errors);

      if (Object.keys(errors).length === 0) {
        callback(values, { resetForm });
      }
    };
  }

  function resetForm() {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }

  function getFieldProps<K extends keyof T>(
    field: K
  ): GetFieldPropsReturn<T, K> {
    return {
      name: field,
      value: values[field],
      onChange: handleChange,
      onBlur: handleBlur,
      error: touched[field] ? errors[field] : undefined,
    };
  }

  function setFieldValue<K extends keyof T>(name: K, value: T[K]) {
    const newValues = {
      ...values,
      [name]: value,
    };
    setValues(newValues);
    const newErrors = { ...errors, [name]: validateField(name, value) };
    const updatedErrors = filterObj(newErrors, (val) => val !== undefined);
    setErrors(updatedErrors);
  }

  return {
    values,
    errors,
    handleSubmit,
    resetForm,
    getFieldProps,
    setFieldValue,
    isValid:
      Object.keys(touched).length > 0 && Object.keys(errors).length === 0,
    isDirty,
  };
}
